// Regras puras do catálogo de permissões (#80506).
//
// O catálogo NÃO mora aqui: vem de GET /permission_groups/catalog, já filtrado
// pelos produtos que a conta contratou. Este arquivo só tem as funções que a
// tela usa em cima dele — assim um produto ou uma permissão nova aparece sem
// deploy de front.
//
// Formato que chega da API (snake_case, sem passar por toCamelcase):
//   { products: [{ app, label, sections: [{ key, label, hint,
//                    items: [{ key, label, hint, default, flag }],
//                    subtabs: [{ key, label, items: [...] }] }] }],
//     scope_dimensions: [{ key, label, hint, kind, dynamic, apps,
//                          options: [{ value, label }], options_by_app,
//                          inline_after: { section, subtab, item } }] }

export const EMPTY_CATALOG = { products: [], scope_dimensions: [] }

/** Identificador global de uma permissão: `app:service`. */
export function permissionId(app, key) {
  return `${app}:${key}`
}

/** Quebra `app:service` de volta em [app, service]. */
export function splitPermissionId(id) {
  const at = id.indexOf(':')
  return [id.slice(0, at), id.slice(at + 1)]
}

/** Itens de uma aba, venham de `items` ou das `subtabs`. */
export function sectionItems(section) {
  if (section.items) return section.items
  if (section.subtabs) return section.subtabs.reduce((all, tab) => all.concat(tab.items), [])
  return []
}

/** Todos os itens de um produto. */
export function productItems(product) {
  return product.sections.reduce((all, section) => all.concat(sectionItems(section)), [])
}

/**
 * As que o catálogo marca como padrão — o mesmo conjunto do grupo Membro
 * (RN 14): ver calendário, ver histórico e a listagem de cada produto
 * contratado. O grupo novo nasce com elas marcadas; permissão nova só entra
 * aqui se o catálogo do backend a marcar como padrão.
 */
export function defaultPermissionIds(products) {
  return products.reduce(
    (all, product) => all.concat(
      productItems(product)
        .filter(item => item.default)
        .map(item => permissionId(product.app, item.key))
    ),
    []
  )
}

export function allPermissionIds(products) {
  return products.reduce(
    (all, product) => all.concat(productItems(product).map(item => permissionId(product.app, item.key))),
    []
  )
}

// ─── Recorte de dado ─────────────────────────────────────────────────────────

/** Rótulo exibido acima das opções. */
export function scopeTitle(dimension) {
  if (dimension.kind === 'companies') return dimension.label

  const [first, ...rest] = dimension.label.split(' ')
  // Sigla continua em caixa alta: "UF da infração", não "uf da infração".
  const head = first.length > 1 && first === first.toUpperCase() ? first : first.toLowerCase()
  return ['Ocultar por', head, ...rest].join(' ')
}

/** Opções válidas de uma dimensão dentro de um produto. */
export function scopeOptions(dimension, app) {
  const byApp = dimension.options_by_app || {}
  return byApp[app] || dimension.options || []
}

export const SCOPE_SEARCH_THRESHOLD = 15

export function filterScopeOptions(options, term) {
  const needle = (term || '').trim().toLowerCase()
  if (!needle) return options

  const digits = needle.replace(/[^0-9a-z]/g, '')
  return options.filter(option => {
    const label = String(option.label || '').toLowerCase()
    const value = String(option.value || '').toLowerCase()
    return label.includes(needle) || value.includes(needle) || (digits.length > 0 && value.includes(digits))
  })
}

export function scopesForApp(dimensions, app) {
  return dimensions.filter(dimension => dimension.apps.includes(app))
}

/** Dimensões desenhadas logo abaixo de uma permissão específica. */
export function inlineScopesFor(dimensions, app, section, item, subtab) {
  return scopesForApp(dimensions, app).filter(dimension => {
    const after = dimension.inline_after || {}
    return after.section === section && after.item === item && (after.subtab || undefined) === subtab
  })
}

// ─── Dependência entre permissões ────────────────────────────────────────────

// O "Ver" é pré-requisito de toda ação sobre o mesmo recurso: não faz sentido
// poder excluir um veículo que a pessoa não enxerga. A relação é derivada da
// própria chave, não anotada item a item, então permissão nova entra na regra
// sozinha. Mesma regra do PermissionCatalog#prerequisite_for no backend, que
// fecha o conjunto para cima ao salvar — aqui é só a amarração dos checkboxes.
export function prerequisiteFor(products, app, key) {
  const product = products.find(item => item.app === app)
  if (!product) return null

  const items = productItems(product)
  const exists = candidate => items.some(item => item.key === candidate)

  if (app !== 'application')
    return key !== 'viewer' && exists('viewer') ? 'viewer' : null

  const slash = key.indexOf('/')
  if (slash < 0) return null

  const prefix = key.slice(0, slash)
  return [`${prefix}/view`, prefix].find(candidate => candidate !== key && exists(candidate)) || null
}

/** As permissões que deixam de valer se esta for desmarcada. */
export function dependentsOf(products, app, key) {
  const product = products.find(item => item.app === app)
  if (!product) return []

  return productItems(product)
    .filter(item => item.key !== key && prerequisiteFor(products, app, item.key) === key)
    .map(item => item.key)
}

/**
 * Marcar uma ação traz o "Ver" dela; desmarcar o "Ver" leva as ações embora.
 * Recebe e devolve um array de ids (o Vue 2 não é reativo com Set).
 */
export function withDependencies(products, ids, changed, checked) {
  const next = new Set(ids)
  const [app, key] = splitPermissionId(changed)

  if (checked) {
    next.add(changed)
    let current = prerequisiteFor(products, app, key)
    const seen = new Set()
    while (current && !seen.has(current)) {
      seen.add(current)
      next.add(permissionId(app, current))
      current = prerequisiteFor(products, app, current)
    }
  } else {
    next.delete(changed)
    // Cascata: as ações que dependiam desta saem, e as que dependiam delas
    // também. Pilha em vez de recursão para não repetir trabalho.
    const stack = dependentsOf(products, app, key)
    while (stack.length > 0) {
      const dependent = stack.pop()
      const id = permissionId(app, dependent)
      if (!next.has(id)) continue

      next.delete(id)
      stack.push(...dependentsOf(products, app, dependent))
    }
  }

  return [...next]
}
